import { useVitals } from '@hook'
import { useAppSelector } from '@store/hooks'
import { FormButton, FormInputField, ModalHeader } from '@ui'
import { getError, getVitalValueColor, getVitalValueInputClassName, isBmiVital, showToast } from '@util'
import { Box, Layer } from 'grommet'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'

type Props = {
  onClose: () => void
  intakeId?: string
}

type VitalInput = {
  vital_type: string
  value: string
  unit: string
}

type FormValues = {
  vitals: VitalInput[]
}

export const AddVital = ({ onClose, intakeId: intakeIdProp }: Props) => {
  const { patientId } = useParams<string>()
  const { intakeId: id } = useParams<string>()
  const [searchParams] = useSearchParams()
  const intakeId = intakeIdProp || searchParams.get('intake') || id
  const { encounterId } = useParams<string>()
  const { vitals: vitalTypes } = useAppSelector(state => state.config)
  const { addVitals } = useVitals({ patientId: patientId || '' })

  // Prepare initial values for all vital types
  const initialVitals: VitalInput[] = useMemo(
    () =>
      vitalTypes?.map(v => ({
        unit: v.unit || '',
        value: '',
        vital_type: v.name
      })),
    [vitalTypes]
  )

  const methods = useForm<FormValues>({
    defaultValues: { vitals: initialVitals }
  })
  const { control, handleSubmit, setValue, watch } = methods

  // Locate the BMI row + the height/weight rows by vital_type.
  const bmiIndex = useMemo(() => initialVitals.findIndex(v => isBmiVital(v.vital_type)), [initialVitals])
  const heightIndex = useMemo(
    () => initialVitals.findIndex(v => v.vital_type.toLowerCase() === 'height'),
    [initialVitals]
  )
  const weightIndex = useMemo(
    () => initialVitals.findIndex(v => v.vital_type.toLowerCase() === 'weight'),
    [initialVitals]
  )

  // Watch the height and weight fields individually so BMI recalculates the
  // instant either changes.
  const height = watch(`vitals.${heightIndex}.value`)
  const weight = watch(`vitals.${weightIndex}.value`)
  const heightUnit = watch(`vitals.${heightIndex}.unit`)
  const bmi = watch(`vitals.${bmiIndex}.value`)

  useEffect(() => {
    if (bmiIndex < 0) return

    const h = Number(height)
    const w = Number(weight)

    if (!height || !weight || !h || !w) {
      setValue(`vitals.${bmiIndex}.value`, '')
      return
    }

    const heightInMeters = heightUnit === 'mm' ? h / 1000 : heightUnit === 'cm' ? h / 100 : h
    const calculated = w / (heightInMeters * heightInMeters)
    setValue(`vitals.${bmiIndex}.value`, calculated.toFixed(1))
  }, [height, weight, bmiIndex, setValue])

  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)
    const now = moment()
    const today = now.format('YYYY-MM-DD')

    // Only submit vitals with a value
    const vitalsToSubmit = data.vitals.filter(v => v.value !== '' && v.value !== null && v.value !== undefined)

    if (vitalsToSubmit.length === 0) {
      showToast('Please enter at least one vital value')
      setSubmitting(false)
      return
    }

    // Compose the vitals array as per the sample payload
    const vitalsArray = vitalsToSubmit?.map(vital => ({
      captured_date: today,
      time: moment(now).format('HH:mm'),
      unit: vital.unit,
      value: Number(vital.value),
      vital_type: vital.vital_type
    }))

    const payload = intakeId
      ? {
          data: {
            attributes: {
              vitals: vitalsArray
            },
            relationships: {
              intake: {
                data: {
                  id: intakeId,
                  type: 'intake'
                }
              },
              patient: {
                data: {
                  id: patientId,
                  type: 'patient'
                }
              }
            },
            type: 'vital_sign'
          }
        }
      : encounterId
        ? {
            data: {
              attributes: {
                vitals: vitalsArray
              },
              relationships: {
                encounter: {
                  data: {
                    id: encounterId,
                    type: 'encounter'
                  }
                },
                patient: {
                  data: {
                    id: patientId,
                    type: 'patient'
                  }
                }
              },
              type: 'vital_sign'
            }
          }
        : {
            data: {
              attributes: {
                vitals: vitalsArray
              },
              relationships: {
                patient: {
                  data: {
                    id: patientId,
                    type: 'patient'
                  }
                }
              },
              type: 'vital_sign'
            }
          }

    try {
      await addVitals(payload)
      showToast('Vitals added successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
    setSubmitting(false)
  }

  return (
    <FormProvider {...methods}>
      <Layer
        margin={{ vertical: '8px' }}
        modal
        onClickOutside={onClose}
        onEsc={onClose}
        position="center"
        style={{ borderRadius: '15px' }}
      >
        <form className="flex flex-col overflow-auto w-full min-w-0 max-w-[700px]" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader onClose={onClose} title="Add Vital Signs" />
          <Box overflow="hidden" pad="medium">
            <div
              className="mb-4"
              style={{
                border: '1px solid #ccc',
                borderRadius: '8px',
                marginBottom: '1rem',
                overflow: 'auto',
                width: '100%'
              }}
            >
              <table
                className="w-full !p-0"
                style={{
                  borderCollapse: 'collapse',
                  overflow: 'auto',
                  width: '100%'
                }}
              >
                <thead>
                  <tr>
                    <th
                      className="text-left py-2 bg-[#F5F5F5] px-4"
                      style={{
                        borderBottom: '1px solid #ccc'
                      }}
                    >
                      Vitals
                    </th>
                    <th
                      className="text-left py-2 bg-[#F5F5F5] px-4"
                      style={{
                        borderBottom: '1px solid #ccc'
                      }}
                    >
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {initialVitals?.map((vital, idx) => {
                    const isBmi = isBmiVital(vital.vital_type)

                    return (
                      <tr key={vital.vital_type}>
                        <td
                          className="py-2 px-4"
                          style={{
                            borderBottom: '1px solid #ccc',
                            textTransform: 'capitalize'
                          }}
                        >
                          {vital.vital_type.replace(/_/g, ' ')}
                        </td>
                        <td
                          className="py-2 px-4"
                          style={{
                            borderBottom: '1px solid #ccc'
                          }}
                        >
                          {isBmi ? (
                            // BMI is derived — render a read-only controlled
                            // input mirroring FormInputField's layout so it
                            // aligns with the other rows (incl. unit suffix).
                            <div className="relative flex w-full items-center" style={{ minWidth: 100 }}>
                              <input
                                className={
                                  'w-full bg-transparent px-4 h-11 text-sm outline-none border-0' +
                                  ' ' +
                                  getVitalValueInputClassName(getVitalValueColor(vitalTypes, vital.vital_type, bmi))
                                }
                                readOnly
                                tabIndex={-1}
                                title="bmi"
                                value={idx === bmiIndex ? bmi : ''}
                              />
                              {vital.unit && (
                                <span className="absolute inset-y-0 right-4 flex items-center justify-center text-sm opacity-30">
                                  {vital.unit}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Controller
                              control={control}
                              name={`vitals.${idx}.value`}
                              render={({ field }) => (
                                <FormInputField
                                  {...field}
                                  inputClassName={getVitalValueInputClassName(
                                    getVitalValueColor(vitalTypes, vital.vital_type, field.value)
                                  )}
                                  label=""
                                  name={field.name}
                                  no_border
                                  placeholder="Enter value"
                                  style={{ minWidth: 100, outlineWidth: '0px' }}
                                  suffix={vital.unit}
                                  type="number"
                                />
                              )}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Box>
          <FormButton isLoading={submitting} onClose={onClose} submit="save" />
        </form>
      </Layer>
    </FormProvider>
  )
}

export default AddVital
